import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * Parameter-Schemas der Berichtsgruppe „Lager- & Artikelanalyse" (Reporting).
 * Werden per POST-Body übergeben (echte JSON-Typen, keine String-Coercion nötig)
 * und speisen zugleich die Report-Registry. Es gibt genau EIN Lager (Entscheidung
 * Tech-Lead 2026-07-16), daher keine Lagerort-Dimension. Bewertung ohne FIFO:
 * gleitender Ø-EK oder Standard-EK. Alle Geldwerte im Ergebnis sind Money
 * (bigint-Mikro, §6) — hier definieren wir nur die Eingabeparameter.
 */

/** ISO-Datum (YYYY-MM-DD) für Stichtag/Zeitraum. */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als YYYY-MM-DD');

/** 4-stellige Warengruppe (ADR 0016). */
const commodityGroupParam = z.number().int().min(0).max(9999);

/** Bewertungsmethode — FIFO nicht verfügbar (keine Chargen-/Layer-Daten). */
export const valuationMethodSchema = z.enum(['MOVING_AVG', 'STANDARD_COST']);
export type ValuationMethod = z.infer<typeof valuationMethodSchema>;

/** Reichweiten-Klassifikation eines Lagerartikels. */
export const stockVelocityClassSchema = z.enum(['ACTIVE', 'SLOW', 'DEAD']);
export type StockVelocityClass = z.infer<typeof stockVelocityClassSchema>;

/** ABC-Klasse nach kumuliertem Wertanteil. */
export const abcClassSchema = z.enum(['A', 'B', 'C']);
export type AbcClass = z.infer<typeof abcClassSchema>;

// ── 1) Bestandsbewertung ──────────────────────────────────────────────────
export const stockValuationParamsSchema = z.object({
  /** Stichtag; default = heute (serverseitig gesetzt, wenn leer). */
  asOf: isoDateSchema.optional(),
  method: valuationMethodSchema.default('MOVING_AVG'),
  /** Gruppierung der Summenzeilen (Lagerort entfällt — nur ein Lager). */
  groupBy: z.enum(['MAIN_GROUP', 'COMMODITY_GROUP', 'NONE']).default('MAIN_GROUP'),
  commodityGroup: commodityGroupParam.optional(),
  includeZeroStock: z.boolean().default(false),
});
export type StockValuationParams = z.infer<typeof stockValuationParamsSchema>;

// ── 2) Reichweiten-/Ladenhüter-Analyse ────────────────────────────────────
export const stockCoverageParamsSchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    /** Ab dieser Reichweite (Tage) gilt ein Artikel als „langsam". */
    slowMovingDays: z.number().int().positive().default(180),
    /** Ab dieser Reichweite (Tage) gilt ein Artikel als „Ladenhüter". */
    deadStockDays: z.number().int().positive().default(365),
    method: valuationMethodSchema.default('MOVING_AVG'),
    commodityGroup: commodityGroupParam.optional(),
  })
  .refine((p) => p.from <= p.to, { message: 'from muss ≤ to sein', path: ['to'] })
  .refine((p) => p.slowMovingDays < p.deadStockDays, {
    message: 'slowMovingDays muss < deadStockDays sein',
    path: ['deadStockDays'],
  });
export type StockCoverageParams = z.infer<typeof stockCoverageParamsSchema>;

// ── 3) Mindestbestand-Unterschreitung ─────────────────────────────────────
export const reorderRequirementParamsSchema = z.object({
  commodityGroup: commodityGroupParam.optional(),
  /** Offene Bestellungen gegenrechnen (Zulauf mindert den Bedarf). */
  netOpenOrders: z.boolean().default(true),
  /** Reservierungen gegenrechnen (mindern den frei verfügbaren Bestand). */
  netReservations: z.boolean().default(true),
});
export type ReorderRequirementParams = z.infer<typeof reorderRequirementParamsSchema>;

// ── 4) Bestandsbewegungen ─────────────────────────────────────────────────
export const stockMovementsReportParamsSchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    articleId: z.string().uuid().optional(),
    commodityGroup: commodityGroupParam.optional(),
    /** Bewegungsart-Filter; leer = alle. */
    type: z.enum(['IN', 'OUT', 'ADJUST']).optional(),
  })
  .refine((p) => p.from <= p.to, { message: 'from muss ≤ to sein', path: ['to'] });
export type StockMovementsReportParams = z.infer<typeof stockMovementsReportParamsSchema>;

// ── 5) ABC-/Verbrauchsanalyse ─────────────────────────────────────────────
export const abcAnalysisParamsSchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    /** Wertbasis: Verbrauch (Menge×EK) oder Umsatz (Verkaufserlös). */
    valueBasis: z.enum(['CONSUMPTION', 'REVENUE']).default('CONSUMPTION'),
    /** Kumulierter Wertanteil (%) bis Klasse A. */
    thresholdA: z.number().min(0).max(100).default(80),
    /** Kumulierter Wertanteil (%) bis Klasse B; Rest = C. */
    thresholdB: z.number().min(0).max(100).default(95),
    method: valuationMethodSchema.default('MOVING_AVG'),
    commodityGroup: commodityGroupParam.optional(),
  })
  .refine((p) => p.from <= p.to, { message: 'from muss ≤ to sein', path: ['to'] })
  .refine((p) => p.thresholdA < p.thresholdB, {
    message: 'thresholdA muss < thresholdB sein',
    path: ['thresholdB'],
  });
export type AbcAnalysisParams = z.infer<typeof abcAnalysisParamsSchema>;

// ── 6) Verfügbarkeit (ATP) ────────────────────────────────────────────────
export const availabilityAtpParamsSchema = z.object({
  articleId: z.string().uuid().optional(),
  commodityGroup: commodityGroupParam.optional(),
  /** Offene Wareneingänge (offene Bestellungen) in die Verfügbarkeit einrechnen. */
  includeOpenReceipts: z.boolean().default(true),
  /** Nur Artikel mit negativer Verfügbarkeit (ATP < 0) ausweisen. */
  onlyShortage: z.boolean().default(false),
});
export type AvailabilityAtpParams = z.infer<typeof availabilityAtpParamsSchema>;

/** Kennung der sechs Berichte dieser Gruppe (Registry + Route). */
export const inventoryReportIdSchema = z.enum([
  'inventory-valuation',
  'stock-coverage',
  'reorder-requirement',
  'stock-movements',
  'abc-analysis',
  'availability-atp',
]);
export type InventoryReportId = z.infer<typeof inventoryReportIdSchema>;

/** Ausgabeformat eines Berichtslaufs. */
export const reportOutputFormatSchema = z.enum(['json', 'csv', 'xlsx', 'pdf']);
export type ReportOutputFormat = z.infer<typeof reportOutputFormatSchema>;

// ══ Ergebnis-Schemas (DTO; Money string-serialisiert, §6/§8) ══════════════════
const nullableMoney = moneySchema.nullable();
const currencyEnum = z.enum(['EUR', 'USD', 'CHF']);

// 1) Bestandsbewertung
export const stockValuationLineSchema = z.object({
  articleId: z.string(),
  number: z.string(),
  name: z.string(),
  commodityGroup: z.number().int().nullable(),
  mainGroupCode: z.number().int().nullable(),
  stockQty: z.number(),
  unitPrice: nullableMoney,
  value: moneySchema,
  missingPrice: z.boolean(),
});
export const stockValuationGroupSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: moneySchema,
  articleCount: z.number().int(),
});
export const stockValuationReportSchema = z.object({
  asOf: z.string(),
  method: valuationMethodSchema,
  groupBy: z.enum(['MAIN_GROUP', 'COMMODITY_GROUP', 'NONE']),
  currency: currencyEnum,
  totalValue: moneySchema,
  totalArticles: z.number().int(),
  groups: z.array(stockValuationGroupSchema),
  lines: z.array(stockValuationLineSchema),
});
export type StockValuationReport = z.infer<typeof stockValuationReportSchema>;

// 2) Reichweiten-/Ladenhüter-Analyse
export const stockCoverageLineSchema = z.object({
  articleId: z.string(),
  number: z.string(),
  name: z.string(),
  stockQty: z.number(),
  consumedQty: z.number(),
  dailyConsumption: z.number(),
  coverageDays: z.number().nullable(),
  velocity: stockVelocityClassSchema,
  boundCapital: moneySchema,
});
export const stockCoverageClassSummarySchema = z.object({
  velocity: stockVelocityClassSchema,
  articleCount: z.number().int(),
  boundCapital: moneySchema,
});
export const stockCoverageReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  windowDays: z.number().int(),
  method: valuationMethodSchema,
  currency: currencyEnum,
  totalBoundCapital: moneySchema,
  classes: z.array(stockCoverageClassSummarySchema),
  lines: z.array(stockCoverageLineSchema),
});
export type StockCoverageReport = z.infer<typeof stockCoverageReportSchema>;

// 3) Mindestbestand-Unterschreitung
export const reorderLineSchema = z.object({
  articleId: z.string(),
  number: z.string(),
  name: z.string(),
  stockQty: z.number(),
  minStock: z.number(),
  reserved: z.number(),
  openOrdered: z.number(),
  projectedAvailable: z.number(),
  reorderQty: z.number(),
});
export const reorderReportSchema = z.object({
  lines: z.array(reorderLineSchema),
  totalBelowMinimum: z.number().int(),
  totalReorderArticles: z.number().int(),
});
export type ReorderReport = z.infer<typeof reorderReportSchema>;

// 4) Bestandsbewegungen
export const stockMovementReportLineSchema = z.object({
  id: z.string(),
  articleId: z.string(),
  articleNumber: z.string(),
  articleName: z.string(),
  occurredAt: z.string(),
  type: z.enum(['IN', 'OUT', 'ADJUST']),
  quantity: z.number(),
  unitCost: nullableMoney,
  value: nullableMoney,
  sourceType: z.string().nullable(),
  sourceRef: z.string().nullable(),
  projectCode: z.string().nullable(),
});
export const stockMovementsReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  currency: currencyEnum,
  lines: z.array(stockMovementReportLineSchema),
  totalInQty: z.number(),
  totalOutQty: z.number(),
  totalAdjustQty: z.number(),
  totalInValue: moneySchema,
  totalOutValue: moneySchema,
});
export type StockMovementsReport = z.infer<typeof stockMovementsReportSchema>;

// 5) ABC-/Verbrauchsanalyse
export const abcLineSchema = z.object({
  articleId: z.string(),
  number: z.string(),
  name: z.string(),
  value: moneySchema,
  valueShare: z.number(),
  cumulativeShare: z.number(),
  abcClass: abcClassSchema,
  frequency: z.number().int(),
});
export const abcClassSummarySchema = z.object({
  abcClass: abcClassSchema,
  articleCount: z.number().int(),
  value: moneySchema,
  valueShare: z.number(),
});
export const abcReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  valueBasis: z.enum(['CONSUMPTION', 'REVENUE']),
  currency: currencyEnum,
  totalValue: moneySchema,
  classes: z.array(abcClassSummarySchema),
  lines: z.array(abcLineSchema),
});
export type AbcReport = z.infer<typeof abcReportSchema>;

// 6) Verfügbarkeit (ATP)
export const atpLineSchema = z.object({
  articleId: z.string(),
  number: z.string(),
  name: z.string(),
  stockQty: z.number(),
  reserved: z.number(),
  openReceipts: z.number(),
  atp: z.number(),
  nextExpectedDate: z.string().nullable(),
});
export const atpReportSchema = z.object({
  lines: z.array(atpLineSchema),
  shortageCount: z.number().int(),
});
export type AtpReport = z.infer<typeof atpReportSchema>;

// ══ Render-Projektion (eine Tabelle je Bericht; speist alle Renderer/Frontend) ══
export const reportColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['text', 'number', 'money', 'percent', 'date', 'int']).optional(),
  align: z.enum(['left', 'right']).optional(),
});
export type ReportColumnDto = z.infer<typeof reportColumnSchema>;

export const reportTableSchema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  currency: z.string(),
  columns: z.array(reportColumnSchema),
  rows: z.array(z.record(z.union([z.string(), z.number(), z.null()]))),
  summary: z.array(z.object({ label: z.string(), value: z.string() })),
});
export type ReportTableDto = z.infer<typeof reportTableSchema>;
